import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Archive, Camera, Images, MoreVertical, Network, Users } from 'lucide-react';
import { AddMediaFlow } from '@/components/media/add-media';
import { useNavDrawer } from './header-actions';
import { cn } from '@/lib/utils';

/**
 * Die Ziele der Leiste unten – die drei Hauptbereiche und nichts sonst.
 *
 * Am Telefon sind vier Felder frei, die Kamera nimmt das fünfte. Damit trägt
 * die Leiste genau, was die Seitenleiste oben ohne Überschrift trägt: die
 * Ahnenkiste (ankommen), das Fotoalbum (alle Bilder), der Stammbaum (alle
 * Menschen) – dazu das Aufnehmen und „Mehr“.
 *
 * „Personen“ stand hier auf Platz zwei und ist jetzt einen Tipp tiefer: im
 * Stammbaum-Bereich als zweiter Weg auf denselben Bestand und in der
 * Schublade unter „Mehr“. Was weichen musste, war die *Wahl* zwischen Liste
 * und Baum an der Stelle, an der beide dasselbe meinen – nicht die Liste.
 *
 * Die Familienfragen standen hier ebenfalls eine Weile. Sie sind kein Weg in
 * den Bestand, sondern etwas, das auf einen wartet – und dafür ist die
 * Startseite der Ort: dort steht die Karte „Offene Fragen der Familie“, und in
 * der Schublade trägt „Familienfragen“ weiterhin seine Zahl.
 */
const TABS = [
  { to: '/', label: 'Ahnenkiste', icon: Archive, end: true },
  { to: '/medien', label: 'Fotoalbum', icon: Images },
] as const;

function TabLink({
  to,
  label,
  icon: Icon,
  end,
}: {
  to: string;
  label: string;
  icon: typeof Users;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'relative flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5 py-2',
          'text-[11px] leading-none transition-colors',
          isActive ? 'text-ink' : 'text-ink-3',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Der aktive Zustand hängt an zwei Dingen, nicht an einem: das
              Zeichen wechselt nach Gold, die Beschriftung wird dunkel und
              halbfett. Eine Farbe allein trüge ihn nicht – auf einer getönten
              Leiste ist der Unterschied zwischen zwei Grautönen bei 20px
              Symbolgröße im Sonnenlicht keiner. */}
          <Icon
            className={cn('size-[22px] transition-colors', isActive && 'text-accent')}
            strokeWidth={isActive ? 2 : 1.75}
            aria-hidden
          />
          <span className={cn('truncate', isActive && 'font-semibold text-ink')}>{label}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * Die Leiste am unteren Rand – fünf gleich breite Felder auf einer Fläche.
 *
 * Ein Balken über die ganze Breite und keine schwebende Pille mehr: die Pille
 * war ein Gegenstand *über* dem Blatt, mit Rändern links und rechts, in denen
 * der Inhalt durchlief – hübsch, aber sie nahm dem Daumen die beiden äußeren
 * Ecken und dem Inhalt eine Zeile mehr, als sie hoch ist. Die Fläche liegt
 * jetzt am Rand auf, in derselben Tönung wie die Seitenleiste am Schreibtisch:
 * die Navigation ist der Rand des Blattes, der Inhalt das Papier.
 *
 * Der Geräteeinzug kommt als Innenabstand dazu, nicht als Abstand darunter –
 * sonst schwebte die Fläche auf Geräten mit Balken am unteren Rand wieder.
 *
 * `max()` und nicht `env()` allein: Safari meldet die Einzüge nur bei
 * `viewport-fit=cover` im Viewport-Meta, und das steht dort bewusst nicht (es
 * schöbe den Inhalt oben unter die Aussparung). Ohne den Sockel säßen die
 * Beschriftungen auf einem iPhone auf der untersten Bildkante, halb unter dem
 * Bedienbalken. Der Sockel gilt immer, ein gemeldeter Geräteeinzug darf ihn
 * überbieten.
 */
export function MobileTabs() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const drawer = useNavDrawer();

  return (
    <>
      <nav
        aria-label="Hauptnavigation"
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 lg:hidden',
          'border-t border-line bg-sunken/95 backdrop-blur-md',
          'pb-[max(0.875rem,env(safe-area-inset-bottom))]',
        )}
      >
        <div className="mx-auto flex h-18 max-w-lg items-stretch px-1">
          <TabLink {...TABS[0]} />
          <TabLink {...TABS[1]} />

          {/*
            Aufnehmen ist eine Handlung und kein Ziel – deshalb ein Knopf und
            kein `NavLink`, und deshalb als einziges Feld gefüllt und dunkel.
            Es steht trotzdem in der Reihe und nicht als runder Knopf darüber:
            am Telefon ist der Weg ins Archiv fast immer der über die Kamera,
            und was man ständig braucht, gehört dorthin, wo der Daumen ohnehin
            hinfährt.

            Ein Tipp öffnet keine Menüliste, sondern große Flächen:
            abfotografieren oder aus der Galerie. Das ist keine
            Unentschlossenheit, sondern der Unterschied zwischen zwei wirklich
            verschiedenen Absichten – ein altes Bild vor sich liegen zu haben
            oder eines schon im Telefon. Wer hier für den Nutzer raten würde,
            schickt ihn in der Hälfte der Fälle in die falsche Systemansicht.
          */}
          <button
            type="button"
            onClick={() => setSheetOpen(true)}
            className={cn(
              'flex min-w-0 flex-1 flex-col items-center gap-1 pt-1.5 pb-2',
              'text-[11px] leading-none text-ink-3 transition-colors active:text-ink',
            )}
          >
            <span
              className={cn(
                'flex size-11 items-center justify-center rounded-full',
                'bg-panel text-panel-ink shadow-float',
                'transition-[scale] active:scale-95',
              )}
            >
              <Camera className="size-[22px]" strokeWidth={1.9} aria-hidden />
            </span>
            <span className="truncate">Aufnehmen</span>
          </button>

          {/* Der Stammbaum steht *hinter* der Kamera: der gefüllte Knopf
              gehört in die Mitte der fünf Felder, und dort teilt er die drei
              Bereiche nun einmal. */}
          <TabLink to="/stammbaum" label="Stammbaum" icon={Network} />

          {/* „Mehr“ öffnet die Schublade mit allen Bereichen. Hervorgehoben,
              solange sie offen steht – sonst wäre es das einzige Feld der
              Leiste, das auf einen Tipp hin nichts an sich ändert. */}
          <button
            type="button"
            onClick={() => drawer?.setOpen(true)}
            aria-expanded={drawer?.open ?? false}
            className={cn(
              'flex min-w-0 flex-1 flex-col items-center justify-center gap-1.5 py-2',
              'text-[11px] leading-none transition-colors',
              drawer?.open ? 'text-ink' : 'text-ink-3',
            )}
          >
            <MoreVertical
              className={cn('size-[22px]', drawer?.open && 'text-accent')}
              strokeWidth={drawer?.open ? 2 : 1.75}
              aria-hidden
            />
            <span className={cn('truncate', drawer?.open && 'font-semibold text-ink')}>Mehr</span>
          </button>
        </div>
      </nav>

      {/* Dasselbe Blatt wie der Knopf „Foto hinzufügen" am Schreibtisch – ein
          Ablauf, eine Fassung. Vorher stand die Auswahl hier ausformuliert und
          in der Medienseite ein zweites Mal; die beiden liefen auseinander,
          sobald jemand eine davon anfasste. */}
      <AddMediaFlow open={sheetOpen} onOpenChange={setSheetOpen} />
    </>
  );
}
